export type SystemStep = {
  step: string
  title: string
  body: string
}

/* 2026-09-08 (compliance, Kevin): Yelp flagged Viridia for review
   solicitation. Step 04 previously read "Follow-ups, reminders, and review
   requests run on their own", and packageIncludes carried "Reputation &
   review management". Both are removed. Do not reinstate any claim to
   request, generate, solicit, automate or manage reviews. */

export const systemSteps: SystemStep[] = [
  {
    step: '01',
    title: 'The lead comes in',
    body: 'Yelp, Google, your website, a referral texting the number off your truck. Every one of them lands in the same place instead of three apps you check at night.',
  },
  {
    step: '02',
    title: 'Something answers, every time',
    body: 'You are on a roof or under a sink. The AI receptionist picks up, and a missed call gets a text back in seconds, while they are still holding the phone.',
  },
  {
    step: '03',
    title: 'The ads keep the top full',
    body: 'Yelp and Google spend gets managed against booked jobs, not clicks. Your Google Business Profile is set up so the people already searching for you actually find you.',
  },
  {
    step: '04',
    title: 'The CRM keeps chasing',
    body: 'Nobody goes cold because you got busy. Follow-ups and reminders run on their own until the job is booked or they say no.',
  },
]

export const packageIncludes: string[] = [
  'Viridia CRM + Mobile app',
  'AI receptionist, built and tuned with you',
  'Missed-call text-back',
  'Business phone number + A2P registration',
  'Pipelines, workflows & automations built with you',
  'Social media + email marketing',
  'Invoicing with QuickBooks sync',
  'One unified inbox for calls, texts, Yelp, Google & Meta',
  'Easy onboarding + a setup call',
  'Monthly support and monthly ad reporting',
]

export type WebsiteTier = {
  name: string
  price: string
  hosting: string
  pages: string
  includes: string[]
  bonus: string[]
  featured?: boolean
}

/* 2026-09-15 (Kevin, three-services restructure): tier descriptions rewritten
   to name what you actually get per tier (Kevin's own framing from the
   handoff), not a generic feature list. Hosting dropped from $30 to $20/mo
   across every tier per the same handoff; "updates included" added since
   that was previously undocumented but always true.

   2026-09-19 (Kevin, website-lane launch): tiers rebuilt to Kevin's offer.
   Starter is 4 pages with NO contact form (tap-to-call / tap-to-text instead);
   the form, Privacy Policy and Terms start at Growth, because collecting
   visitor info is what requires the legal pages. Each tier's "bonus" list is
   the launch promo: it borrows one or two items from the tier above so every
   buyer feels they got a deal. Turnaround stated as live in 2 to 3 days
   (Premium 3 to 5). Keep claims to "more for Google to read" -- never promise
   rankings or traffic. */
export const websiteTiers: WebsiteTier[] = [
  {
    name: 'Starter',
    price: '$500',
    hosting: '$20/month hosting · updates included',
    pages: '4 pages · live in 2 to 3 days',
    includes: [
      'Home, Services, About or Gallery, and Contact',
      'Tap-to-call and tap-to-text buttons on every page',
      'Mobile responsive',
      'On-page SEO built in',
      'Your colors, your logo, your photos',
    ],
    bonus: [
      'Google Search Console setup and sitemap submitted, so Google indexes you from day one',
      'First month of hosting free',
    ],
  },
  {
    name: 'Growth',
    price: '$850',
    hosting: '$20/month hosting · updates included',
    pages: '5 pages · live in 2 to 3 days',
    featured: true,
    includes: [
      'Home, About, Services, Gallery, and Contact',
      'Contact form that sends leads straight to your phone and email',
      'Privacy Policy and Terms pages',
      'More custom design with interactive touches: before-and-after photo sliders, a service-area map, a call button that follows visitors down the page',
      'Google Search Console setup',
      'Conversion-focused calls to action',
    ],
    bonus: [
      'Schema markup, so Google reads your trade, hours, and service area',
      'One extra service page',
      'First month of hosting free',
    ],
  },
  {
    name: 'Premium',
    price: '$1,200+',
    hosting: '$20/month hosting · updates included',
    pages: '7+ pages · live in 3 to 5 days',
    includes: [
      'Everything in Growth',
      'A page for each service and each city you cover',
      'Full custom design',
      'Blog setup',
      'Schema markup',
      'More for Google to read, more ways to be found',
    ],
    bonus: [
      'A business email on your domain (you@yourbusiness.com)',
      'First 3 months of hosting free',
    ],
  },
]

/* Domain terms, shown under the tiers. 2026-09-19: Viridia registers and
   holds the domain; the transfer fee is disclosed up front on purpose so it
   matches the Stripe checkout terms and never surprises a buyer. */
export const domainTerms =
  'Domain included free on every tier. We register it, manage it, and keep it renewed while you are hosted with us. Want it moved to your own account? We transfer it anytime for a one-time $99 fee. Prefer to buy it yourself? We send you a free step-by-step guide.'

export type ServiceCard = {
  name: string
  forWho: string
  price: string
  priceNote?: string
  to: string
  cta: string
}

/* Homepage "Three ways we work with you" + the /pricing three-column strip
   both read from this one list so the ladder never drifts between pages.
   Order is fixed by the 2026-09-14 handoff: Viridia System, Yelp Ads
   Management, Websites. */
export const threeServices: ServiceCard[] = [
  {
    name: 'The Viridia System',
    forWho: 'For owners who want every lead answered and the back office running on its own.',
    price: '$897',
    priceNote: '/month',
    to: '/pricing#viridia-system',
    cta: 'See what is included',
  },
  {
    name: 'Yelp Ads Management',
    forWho: 'For owners already spending on Yelp who want it run by someone who used to work there.',
    price: '15%',
    priceNote: 'of ad spend, $200/mo min',
    to: '/yelp-ads',
    cta: 'See how it works',
  },
  {
    name: 'Websites',
    forWho: 'For owners who need a site that looks like a developer built it, live in 2 to 3 days.',
    price: 'From $500',
    priceNote: 'one-time',
    to: '/websites',
    cta: 'See examples',
  },
]
